<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">短網址詳情</h1>
      <div class="page-actions">
        <button class="btn btn-default" @click="handleBack">
          <i class="ri-arrow-left-line"></i>
          返回列表
        </button>
        <button class="btn btn-primary" @click="handleEdit">
          <i class="ri-edit-line"></i>
          編輯
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-container">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="handleBack">返回列表</button>
    </div>

    <div v-else-if="shortUrl" class="detail-container">
      <div class="detail-section">
        <h3>基本資訊</h3>
        <div class="detail-grid">
          <div class="detail-item">
            <label>設定名稱</label>
            <div>{{ shortUrl.urlName || shortUrl.configName || '-' }}</div>
          </div>
          <div class="detail-item">
            <label>短代碼</label>
            <div><code>{{ shortUrl.shortCode }}</code></div>
          </div>
          <div class="detail-item">
            <label>完整短網址</label>
            <div class="url-display-wrapper">
              <code class="url-code">{{ shortUrl.shortUrl || shortUrl.fullShortUrl || '-' }}</code>
              <button class="btn btn-sm btn-secondary" @click="copyShortUrl" title="複製短網址">
                <i class="ri-file-copy-line"></i>
              </button>
            </div>
          </div>
          <div class="detail-item">
            <label>目標網址</label>
            <div class="url-display-wrapper">
              <code class="url-code">{{ shortUrl.targetUrl || '-' }}</code>
              <button class="btn btn-sm btn-secondary" @click="copyTargetUrl" title="複製目標網址">
                <i class="ri-file-copy-line"></i>
              </button>
            </div>
          </div>
          <div class="detail-item">
            <label>描述</label>
            <div>{{ shortUrl.description || '-' }}</div>
          </div>
          <div class="detail-item">
            <label>來源類型</label>
            <div>{{ shortUrl.sourceType || '-' }}</div>
          </div>
          <div class="detail-item">
            <label>來源代碼</label>
            <div>{{ shortUrl.sourceCode || '-' }}</div>
          </div>
          <div class="detail-item">
            <label>狀態</label>
            <div>
              <span :class="['badge', shortUrl.isActive ? 'badge-success' : 'badge-secondary']">
                {{ shortUrl.isActive ? '啟用' : '停用' }}
              </span>
            </div>
          </div>
          <div class="detail-item" v-if="shortUrl.expiresAt">
            <label>過期時間</label>
            <div>{{ formatDate(shortUrl.expiresAt) }}</div>
          </div>
          <div class="detail-item" v-else>
            <label>過期時間</label>
            <div class="text-muted">永久有效</div>
          </div>
        </div>
      </div>

      <div class="detail-section" v-if="shortUrl.autoTags && shortUrl.autoTags.length > 0">
        <h3>自動標籤</h3>
        <div class="tags-display">
          <span 
            v-for="tagId in shortUrl.autoTags" 
            :key="tagId" 
            class="tag-badge"
          >
            {{ tagNameMap[tagId] || tagId }}
          </span>
        </div>
        <p class="text-muted" style="margin-top: 8px; font-size: 13px;">
          <i class="ri-information-line"></i>
          當會員透過此短網址點擊並完成指定動作時，會自動貼上這些標籤
        </p>
      </div>

      <div class="detail-section">
        <h3>統計資料</h3>
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-value">{{ shortUrl.clickCount || shortUrl.totalClicks || 0 }}</div>
            <div class="stat-label">總點擊次數</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ shortUrl.uniqueClickCount || shortUrl.uniqueClicks || 0 }}</div>
            <div class="stat-label">唯一點擊數</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ shortUrl.addFriendCount || 0 }}</div>
            <div class="stat-label">加好友次數</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ shortUrl.bindCount || 0 }}</div>
            <div class="stat-label">綁定次數</div>
          </div>
        </div>
      </div>

      <div class="detail-section">
        <h3>時間資訊</h3>
        <div class="detail-grid">
          <div class="detail-item">
            <label>建立時間</label>
            <div>{{ formatDate(shortUrl.createdAt) }}</div>
          </div>
          <div class="detail-item">
            <label>更新時間</label>
            <div>{{ formatDate(shortUrl.updatedAt) }}</div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="error-container">
      <p class="error-message">找不到短網址資料</p>
      <button class="btn btn-secondary" @click="handleBack">返回列表</button>
    </div>
  </div>
</template>

<script>
import lineShortUrlApi from '@/api/modules/lineShortUrl.js';
import { tagService } from '@/api/modules/tags.js';
import { showSuccess, showError } from '@/utils/swal.js';

export default {
  name: 'LineShortUrlDetail',
  data() {
    return {
      loading: false,
      error: null,
      shortUrl: null,
      tagNameMap: {}
    };
  },
  mounted() {
    this.loadShortUrl();
  },
  methods: {
    async loadShortUrl() {
      this.loading = true;
      this.error = null;
      try {
        const response = await lineShortUrlApi.get(this.$route.params.id);
        if (response && response.success && response.data) {
          this.shortUrl = response.data;
          await this.loadTagNames(this.shortUrl.autoTags);
        } else {
          this.error = '找不到短網址資料';
        }
      } catch (error) {
        console.error('載入短網址失敗:', error);
        const errorMessage = error?.response?.data?.message || error?.message || '載入失敗';
        this.error = errorMessage;
      } finally {
        this.loading = false;
      }
    },

    async loadTagNames(tagIds) {
      if (!tagIds || tagIds.length === 0) return;
      try {
        const response = await tagService.getTags();
        if (response && response.success && response.data) {
          const tags = Array.isArray(response.data) ? response.data : (response.data.items || []);
          const map = {};
          tags.forEach(tag => {
            map[tag.id] = tag.tagName || tag.name || tag.id;
          });
          this.tagNameMap = map;
        }
      } catch (error) {
        console.warn('載入標籤名稱失敗:', error);
      }
    },
    
    async copyShortUrl() {
      const url = this.shortUrl?.shortUrl || this.shortUrl?.fullShortUrl;
      if (!url) {
        await showError('複製失敗', '沒有可複製的短網址');
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
        await showSuccess('已複製', url, 1500);
      } catch (error) {
        console.error('複製失敗:', error);
        await showError('複製失敗', '請手動複製網址');
      }
    },
    
    async copyTargetUrl() {
      const url = this.shortUrl?.targetUrl;
      if (!url) {
        await showError('複製失敗', '沒有可複製的目標網址');
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
        await showSuccess('已複製', url, 1500);
      } catch (error) {
        console.error('複製失敗:', error);
        await showError('複製失敗', '請手動複製網址');
      }
    },
    
    handleBack() {
      this.$router.push({ name: 'ClientLineShortUrlList' });
    },
    
    handleEdit() {
      this.$router.push({ 
        name: 'ClientEditLineShortUrl', 
        params: { id: this.$route.params.id } 
      });
    },
    
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.page-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-title {
  font-size: 24px;
  margin: 0;
}

.page-actions {
  display: flex;
  gap: 12px;
}

.detail-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.detail-section {
  padding: 24px;
  border-bottom: 1px solid #f0f0f0;
}

.detail-section:last-child {
  border-bottom: none;
}

.detail-section h3 {
  font-size: 18px;
  margin-bottom: 16px;
  font-weight: 600;
  color: #333;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.detail-item label {
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
  color: #666;
  font-size: 14px;
}

.detail-item div {
  color: #333;
  word-break: break-word;
}

code {
  background-color: #f5f5f5;
  padding: 4px 8px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #e83e8c;
}

.url-display-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.url-code {
  flex: 1;
  word-break: break-all;
  max-width: calc(100% - 40px);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.stat-card {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
}

.stat-value {
  font-size: 32px;
  font-weight: bold;
  color: #007bff;
  margin-bottom: 8px;
}

.stat-label {
  color: #666;
  font-size: 14px;
}

.badge {
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 14px;
  display: inline-block;
}

.badge-success {
  background-color: #28a745;
  color: white;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
}

.btn {
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  transition: all 0.3s;
}

.btn-default {
  background-color: #f8f9fa;
  color: #333;
  border: 1px solid #dee2e6;
}

.btn-default:hover {
  background-color: #e9ecef;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover {
  background-color: #0056b3;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #5a6268;
}

.btn-sm {
  padding: 4px 12px;
  font-size: 13px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.text-muted {
  color: #6c757d;
}

.loading-container,
.error-container {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.error-message {
  color: #dc3545;
  font-size: 16px;
  margin-bottom: 20px;
}

.tags-display {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag-badge {
  background-color: #e7f3ff;
  color: #1890ff;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 13px;
  border: 1px solid #91d5ff;
}

@media (max-width: 768px) {
  .page-container {
    padding: 12px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
